import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Collapse, Grid } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import Alert from '@material-ui/lab/Alert';
import { confirm, showAlert } from 'actions/app';
import * as BulkEmailActions from 'actions/bulkEmail';
import { StatusManager } from 'common/components/StatusManager';
import { BulkEmailDrawerSequencesPreview } from 'features/bulk-email/sequences/components/BulkEmailDrawerSequencesPreview';
import { useBulkQuotaRestriction } from 'hooks/bulkQuotaRestriction';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { EntityRoutes } from 'routes/constants';
import { Permissions, userHasPermission } from 'services/Authorization';
import strings from 'strings';
import { FPIconButton } from 'UI/components/atoms';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import { When } from 'UI/components/atoms/When';
import DialogDetailsBody from 'UI/components/molecules/DialogDetailsBody';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import ScheduleDialog from 'UI/components/organisms/BulkEmail/ScheduleDialog';
import BulkEmailForm from 'UI/components/organisms/BulkEmailForm';
import { BulkDrawerFormFieldsMap } from 'UI/components/organisms/BulkEmailForm/fields';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import {
  BulkMarketingOptions,
  BulkMenuItems,
  BulkScopes,
  PreviewTypes,
  registerMultipleCheckboxOptions,
  StringsBooleanMap
} from 'UI/constants/defaults';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { UIStatus } from 'UI/constants/status';
import { SvgArrowLeft } from 'UI/res';
import { convertEmailToPlainText, dateFormatter, getId, getRemainingTimeToDate } from 'UI/utils';
import { getActionDataToDispatch } from 'UI/utils/bulkEmail';
import { encryptId } from 'UI/utils/encrypt';
import { preloadFromBackend } from 'UI/utils/forms';

import { DiscardBulkDialog } from '../BulkEmail/DiscardBulkDialog';
import RestrictionDialog from '../BulkEmail/RestrictionDialog/RestrictionDialog';

import { useBulkSmartTags } from './BulkEmailDrawerSmartTags.hook';
import { useBulkDrawerCommonStyles, useStyles } from './styles';
import {
  isSmartTagsErrorActive,
  validateBulkSequenceIsFilledCorrectly,
  validateHasInvalidAttempts
} from './utils';

const {
  setBulkEmailStatus,
  setBulkEmailToInitial,
  setBulkRestrictionToInitial,
  startRemovingAllAttachments
} = BulkEmailActions;

const {
  forms: { validations: validationsCopies, fields: fieldsCopies, bulkDrawer: drawerCopies },
  schedule: { bulkFormAlert },
  dialogs: { sendBulk: dialogSendCopies, sendBulkSequence: sendSequenceCopies }
} = strings.bulkEmails;

const arrayIsTruthy = arr => Array.isArray(arr) && arr.length > 0;

/**
 * @param {Object} props
 * @param {Object} props.classes - Styles classes for the component
 * @param {Function} props.onOpenScheduleDialog - Callback to open the schedule dialog
 * @param {string} props.sendDate - The scheduled send date
 */
const ScheduleInfoAlert = ({ classes, onOpenScheduleDialog, sendDate }) => {
  const { quantity, unitOfTime } = getRemainingTimeToDate(sendDate) || {};
  const formattedDate = dateFormatter(sendDate, ({ MonthDayYearTime }) => MonthDayYearTime);
  const alertMessage =
    quantity && unitOfTime
      ? strings.formatString(bulkFormAlert.remainingTime, { quantity, unitOfTime, formattedDate })
      : bulkFormAlert.scheduledFailed;
  return (
    <Alert
      action={
        <FPActionButton
          size="small"
          onClick={onOpenScheduleDialog}
          text="Reschedule"
          variant="outlined"
        />
      }
      className={classes.scheduleInfo}
      severity={quantity && unitOfTime ? 'info' : 'warning'}
    >
      {alertMessage}
    </Alert>
  );
};

/**
 * Bulk email drawer component for composing and managing bulk emails
 * @param {Object} props
 * @param {boolean} [props.loadLastBulkEmail=false] - Whether to load the last bulk email on open
 * @param {Function} props.onCloseDrawer - Callback to close the drawer
 * @param {Function} [props.onDiscardChanges=null] - Callback when discarding changes
 * @param {Function} [props.onSetSelectionToInitial=() => {}] - Callback to reset selection to initial state
 * @param {Object} [props.queryParams=null] - Query parameters for the bulk email
 * @param {Object} [props.searchProject=null] - Search project data to prefill
 * @param {Object} [props.selectedItems=null] - Selected items for the bulk email
 * @param {boolean} [props.shouldComposeBulkSequence=false] - Whether to compose a bulk sequence
 */
const BulkEmailDrawer = ({
  loadLastBulkEmail = false,
  onCloseDrawer,
  onDiscardChanges = null,
  onSetSelectionToInitial = () => {},
  queryParams = null,
  searchProject = null,
  selectedItems = null,
  shouldComposeBulkSequence = false
}) => {
  const contentWrapperRef = useRef(null);
  const commonClasses = useBulkDrawerCommonStyles();
  const classes = useStyles();

  const [uiState, setUiState] = useState({
    isFormLoading: false,
    shouldShowScheduleDialog: false,
    shouldShowDiscardDialog: false,
    shouldShowSequencesPreview: false
  });
  const dispatch = useDispatch();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isBulkEmailAIEnabled =
    checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailAI) &&
    userHasPermission(Permissions.BulkEmail.AIBuilder);

  // TODO: possibly, remove sent prop from bulk email domain store as it's being replaced by bulkEmailInPreview
  const {
    domain: {
      template: { id: templateId, emailExcludedAttachments, name: templateName },
      attachments,
      bulkEmailInPreview
    },
    ui: { selectedMenuItem, status: bulkEmailStatus }
  } = useSelector(state => state.bulkEmail);
  const { sendDate } = bulkEmailInPreview.data;

  const isScheduleBeingEdited =
    selectedMenuItem === BulkMenuItems.Schedule && bulkEmailInPreview.edit;
  const isDraftBeingEdited = selectedMenuItem === BulkMenuItems.Drafts && bulkEmailInPreview.edit;
  const bulkRestrictionRule = bulkEmailStatus.bulkRestriction?.rule;
  const defaultValues = useMemo(() => {
    const preloadValues = { values: {}, fieldsMap: {} };

    if (searchProject) {
      preloadValues.values.searchProject = searchProject;
      preloadValues.fieldsMap[BulkDrawerFormFieldsMap.To.key] = BulkDrawerFormFieldsMap.To;
    }

    if (shouldComposeBulkSequence) {
      preloadValues.values.attempts = [getId()];
      preloadValues.fieldsMap[BulkDrawerFormFieldsMap.Attempts.key] =
        BulkDrawerFormFieldsMap.Attempts;
    }

    if (Object.keys(preloadValues.values).length > 0) {
      return preloadFromBackend({ ...preloadValues.values }, { ...preloadValues.fieldsMap });
    }

    return bulkEmailInPreview.edit
      ? preloadFromBackend(bulkEmailInPreview.data, BulkDrawerFormFieldsMap)
      : {};
  }, [bulkEmailInPreview, searchProject, shouldComposeBulkSequence]);

  const form = useForm({ defaultValues });
  const {
    errors,
    getValues,
    setValue,
    handleSubmit,
    register,
    reset,
    triggerValidation,
    unregister,
    watch
  } = form;
  const watchers = watch([
    BulkDrawerFormFieldsMap.Attempts.key,
    BulkDrawerFormFieldsMap.BulkScope.key,
    BulkDrawerFormFieldsMap.EmailBody.key,
    BulkDrawerFormFieldsMap.JobOrderRecruitment.key,
    BulkDrawerFormFieldsMap.MarketCandidates.key,
    BulkDrawerFormFieldsMap.Subject.key,
    BulkDrawerFormFieldsMap.To.key,
    BulkDrawerFormFieldsMap.BulkQuota.key
  ]);

  const searchProjectSize = watchers.to?.bulkableItems;

  const shouldDisablePreviewSequenceButton =
    !validateBulkSequenceIsFilledCorrectly({ errors, watch }) || validateHasInvalidAttempts(watch);

  const shouldDisablePreviewButton =
    !watchers.to?.id ||
    !watchers.subject ||
    !watchers.emailBody ||
    isSmartTagsErrorActive(errors) ||
    bulkEmailStatus.loading;

  const onFetchingSmartTags = useCallback(
    ({ loading }) => setUiState(prev => ({ ...prev, isFormLoading: loading })),
    []
  );

  const smartTagsRegistrationConfig = useMemo(
    () => ({
      subject: {
        key: BulkDrawerFormFieldsMap.Subject.key,
        requiredMessage: validationsCopies.subject.required,
        fieldName: 'subject'
      },
      emailBody: {
        key: BulkDrawerFormFieldsMap.EmailBody.key,
        requiredMessage: validationsCopies.emailBody.required,
        fieldName: 'email body'
      }
    }),
    []
  );

  useBulkSmartTags({
    getValues,
    onFetchingSmartTags,
    register,
    registrationConfig: smartTagsRegistrationConfig,
    setValue
  });
  const { bulkQuota, bulkQuotaRestriction, notifyBulkQuotaRestriction, remainingQuota } =
    useBulkQuotaRestriction({ searchProjectSize });

  useEffect(() => {
    return () => {
      reset({});
      localStorage.removeItem(PreviewTypes.BulkEmail);
    };
  }, [reset]);

  useEffect(() => {
    if (shouldComposeBulkSequence) {
      register({ name: BulkDrawerFormFieldsMap.Attempts.key });
    }

    return () => {
      unregister(BulkDrawerFormFieldsMap.Attempts.key);
    };
  }, [register, shouldComposeBulkSequence, unregister]);

  useEffect(() => {
    if (!bulkEmailInPreview.edit) return;

    const { emailConfig: config, promptId, sendDate: previewSendDate } = bulkEmailInPreview.data;

    if (!config) return;

    setUiState(prev => ({ ...prev, isFormLoading: true }));
    if (promptId)
      register({
        name: BulkDrawerFormFieldsMap.AIPromptId.key
      });
    if (config.blockResend) {
      register(
        { name: BulkDrawerFormFieldsMap.ResendTime.key },
        { required: validationsCopies.resendTime.required }
      );
    }
    if (previewSendDate) {
      register(
        { name: BulkDrawerFormFieldsMap.ScheduleDate.key },
        { required: validationsCopies.scheduleDate.required }
      );
      register(
        { name: BulkDrawerFormFieldsMap.ScheduleTime.key },
        { required: validationsCopies.scheduleTime.required }
      );
    }
    if (config.bulkType) {
      const registerByTypes = {
        [BulkScopes.Marketing]: () => {
          register(
            { name: BulkDrawerFormFieldsMap.MarketCandidates.key },
            { required: validationsCopies.marketCandidates.required }
          );
          registerMultipleCheckboxOptions({ options: BulkMarketingOptions, register });
        },
        [BulkScopes.Recruiting]: () => {
          register(
            { name: BulkDrawerFormFieldsMap.JobOrderRecruitment.key },
            { required: validationsCopies.jobOrdersToRecruit.required }
          );
          register({ name: BulkDrawerFormFieldsMap.AllowRecruitHirings.key });
        },
        [BulkScopes.General]: () => {
          register(
            { name: BulkDrawerFormFieldsMap.GeneralBulkCategory.key },
            { required: validationsCopies.generalBulkCategory.required }
          );
        }
      };
      registerByTypes[config.bulkType.id] && registerByTypes[config.bulkType.id]();
    }
    setUiState(prev => ({ ...prev, isFormLoading: false }));
  }, [bulkEmailInPreview, register, dispatch, triggerValidation]);

  useEffect(() => {
    if (!bulkEmailStatus.loading && (bulkEmailStatus.completed || bulkEmailStatus.sending)) {
      dispatch(setBulkEmailStatus({ completed: false }));
    }
  }, [bulkEmailStatus.completed, bulkEmailStatus.loading, bulkEmailStatus.sending, dispatch]);

  useEffect(() => {
    if (bulkRestrictionRule) {
      setUiState(prev => ({
        ...prev,
        shouldShowScheduleDialog: false,
        shouldShowDiscardDialog: false
      }));
    }
  }, [bulkRestrictionRule]);

  const handleSubmitBulkEmail = useCallback(
    /**
     * @param {Object} formData
     * @param {Object} [options]
     * @param {boolean} [options.saveAsDraft=false]
     * @param {boolean} [options.saveAsSchedule=false]
     * @param {boolean} [options.sendNow=false]
     * @param {boolean} [options.unregisterScheduleFields]
     */
    (formData, options) => {
      const plain = convertEmailToPlainText(formData.emailBody);
      const bulkId =
        bulkEmailInPreview.edit && bulkEmailInPreview.data?.id ? bulkEmailInPreview.data.id : null;
      const sendScheduledBulk =
        bulkEmailInPreview.edit &&
        selectedMenuItem === BulkMenuItems.Schedule &&
        !options?.saveAsSchedule &&
        bulkId;
      dispatch(setBulkEmailStatus({ loading: true }));

      const shouldLoadLastEmailOnList =
        loadLastBulkEmail &&
        (selectedMenuItem === BulkMenuItems.Drafts ||
          selectedMenuItem === BulkMenuItems.Schedule ||
          (selectedMenuItem === BulkMenuItems.Sent && !options?.saveAsDraft));

      const actionOptions = {
        loadLast: shouldLoadLastEmailOnList,
        closeBulkDrawer: onCloseDrawer,
        setSelectionToInitial: onSetSelectionToInitial
      };

      const actionData = getActionDataToDispatch(
        {
          ...formData,
          saveAsDraft: options?.saveAsDraft,
          saveAsSchedule: options?.saveAsSchedule,
          bulkId,
          text: plain ? plain.trim() : 'This message has no text content',
          blockResend: StringsBooleanMap[formData.blockResend],
          selectedItems,
          queryParams
        },
        {
          attachments,
          bulkEmailInPreview,
          emailExcludedAttachments,
          isBulkEmailAIEnabled,
          selectedMenuItem,
          sendScheduledBulk,
          templateId,
          shouldComposeBulkSequence
        }
      );

      if (actionData?.alert) {
        dispatch(showAlert(actionData.alert));
        dispatch(BulkEmailActions.setBulkEmailStatusToInitial());
      } else {
        const actionToExecute = BulkEmailActions[actionData.name];
        dispatch(actionToExecute(actionData.bulkParams, actionOptions));
      }
    },
    [
      attachments,
      bulkEmailInPreview,
      dispatch,
      emailExcludedAttachments,
      isBulkEmailAIEnabled,
      shouldComposeBulkSequence,
      loadLastBulkEmail,
      onCloseDrawer,
      onSetSelectionToInitial,
      queryParams,
      selectedItems,
      selectedMenuItem,
      templateId
    ]
  );

  const handleToggleDiscardDialog = shouldShow => () =>
    setUiState(prev => ({ ...prev, shouldShowDiscardDialog: shouldShow }));

  const joinRowDetails = items => {
    if (!items) return { visible: false };
    const itemsNames = items.map(item => item.title);
    return {
      detail: itemsNames.join(', '),
      visible: itemsNames.length > 0
    };
  };

  const sendDialogDetails = [
    { title: 'To:', detail: `${watchers?.to?.name} (${watchers?.to?.bulkableItems})` },
    {
      title: 'Bulk Type:',
      detail: fieldsCopies.bulkType.typeAndDescription[watchers.bulkScope?.title]
    },
    {
      title: 'Candidates to Market:',
      ...joinRowDetails(watchers.marketCandidates),
      visible: arrayIsTruthy(getValues(BulkDrawerFormFieldsMap.MarketCandidates))
    },
    {
      title: 'Job Orders to Recruit:',
      ...joinRowDetails(watchers.jobOrderRecruitment),
      visible: arrayIsTruthy(getValues(BulkDrawerFormFieldsMap.JobOrderRecruitment))
    },
    { title: 'Template:', detail: templateName, visible: !!templateName }
  ];

  const onSubmit = formData => {
    if (bulkQuotaRestriction) {
      notifyBulkQuotaRestriction();
      return;
    }

    const shouldShowSendSequencesDialog =
      shouldComposeBulkSequence && formData[BulkDrawerFormFieldsMap.Attempts.key]?.length > 0;

    const finalDialogProps = shouldShowSendSequencesDialog
      ? sendSequenceCopies
      : {
          ...dialogSendCopies,
          body: <DialogDetailsBody details={sendDialogDetails} />
        };

    dispatch(
      confirm({
        ...finalDialogProps,
        severity: 'warning',
        withButtons: 'YesNo',
        onConfirm: ok => ok && handleSubmitBulkEmail(formData)
      })
    );
  };

  const handlePreviewBulkEmail = () => {
    const emailData = {
      emailBody: watchers.emailBody,
      searchProjectId: encryptId(watchers.to.id),
      subject: watchers.subject,
      template: templateName,
      attachments,
      queryParams,
      selectedItems
    };
    localStorage.setItem(PreviewTypes.BulkEmail, JSON.stringify(emailData));
    window.open(
      `${window.location.origin}${EntityRoutes.Preview}?type=${PreviewTypes.BulkEmail}`,
      '_blank'
    );
  };

  const togglers = {
    scheduleDialog: open => () => setUiState(prev => ({ ...prev, shouldShowScheduleDialog: open })),
    sequencesPreview: open => () => {
      if (contentWrapperRef.current && contentWrapperRef.current.firstChild) {
        contentWrapperRef.current.firstChild.scrollTo({ top: 0, behavior: 'instant' });
      }
      setUiState(prev => ({ ...prev, shouldShowSequencesPreview: open }));
    }
  };

  const handleSchedule = async () => {
    dispatch(setBulkRestrictionToInitial());
    await triggerValidation();
    const isErrorsEmpty = Object.keys(errors).length === 0;
    isErrorsEmpty && togglers.scheduleDialog(true)();
  };

  const handlers = {
    onSaveChanges: async () => {
      if (selectedMenuItem !== BulkMenuItems.Drafts) {
        await triggerValidation();
        const isErrorsEmpty = Object.keys(errors).length === 0;
        if (!isErrorsEmpty) return;
      }

      handleSubmitBulkEmail(getValues(), {
        saveAsDraft: isDraftBeingEdited,
        saveAsSchedule: isScheduleBeingEdited
      });
    },
    onSend: handleSubmit(onSubmit),
    onSchedule: handleSchedule,
    onCancel: handleToggleDiscardDialog(true),
    onPreview: handlePreviewBulkEmail
  };

  const handleTimeRestrictionDialogReschedule = () => {
    dispatch(setBulkRestrictionToInitial());
    togglers.scheduleDialog(true)();
  };

  const handleSaveDraftDialog = () => {
    dispatch(setBulkRestrictionToInitial());
    handleSubmitBulkEmail(getValues(), { saveAsDraft: true });
    setUiState(prev => ({ ...prev, shouldShowDiscardDialog: false }));
    onCloseDrawer && onCloseDrawer();
  };

  const handleDiscardBulk = () => {
    setUiState(prev => ({ ...prev, shouldShowDiscardDialog: false }));
    if (onDiscardChanges) {
      onDiscardChanges();
    } else {
      dispatch(setBulkEmailToInitial());
      dispatch(startRemovingAllAttachments());
    }
    onCloseDrawer && onCloseDrawer();
  };

  const SubmitBulkOptions = [
    {
      title: drawerCopies.buttons.sendBulk,
      action: handlers.onSend,
      visible: true
    },
    {
      title: strings.formatString(drawerCopies.buttons.schedule.title, {
        preposition:
          bulkEmailInPreview.edit && selectedMenuItem === BulkMenuItems.Schedule
            ? drawerCopies.buttons.schedule.prepositions.reschedule
            : drawerCopies.buttons.schedule.prepositions.schedule
      }),
      action: handlers.onSchedule,
      visible: !shouldComposeBulkSequence
    },
    {
      title: drawerCopies.buttons.save,
      action: handlers.onSaveChanges,
      visible: bulkEmailInPreview.edit
    }
  ];

  const SubmitButtonIcon = useCallback(() => {
    if (bulkEmailStatus.loading) {
      return <CircularProgress size={20} color="inherit" />;
    }

    return shouldComposeBulkSequence ? null : <ArrowDropDown />;
  }, [bulkEmailStatus.loading, shouldComposeBulkSequence]);

  const drawerButtonDefinitions = [
    {
      props: {
        text: drawerCopies.buttons.cancel,
        variant: 'outlined',
        onClick: handlers.onCancel
      },
      visible: !uiState.shouldShowSequencesPreview
    },
    {
      props: {
        disabled: shouldDisablePreviewSequenceButton,
        text: drawerCopies.buttons.previewSequences,
        variant: 'contained',
        onClick: togglers.sequencesPreview(true)
      },
      visible:
        !!watchers[BulkDrawerFormFieldsMap.Attempts.key] && !uiState.shouldShowSequencesPreview
    },
    {
      props: {
        text: drawerCopies.buttons.preview,
        variant: 'outlined',
        onClick: handlers.onPreview,
        disabled: shouldDisablePreviewButton
      },
      visible:
        !watchers[BulkDrawerFormFieldsMap.Attempts.key] && !uiState.shouldShowSequencesPreview
    },
    {
      props: {
        text: drawerCopies.buttons.goBack,
        disabled: bulkEmailStatus.loading,
        variant: 'outlined',
        onClick: togglers.sequencesPreview(false)
      },
      visible: uiState.shouldShowSequencesPreview
    },
    {
      props: {
        className: classes.primaryButton,
        disabled: bulkEmailStatus.loading,
        icon: SubmitButtonIcon,
        iconPosition: 'right',
        text: drawerCopies.buttons.sendSequence,
        onClick: handlers.onSend
      },
      visible: uiState.shouldShowSequencesPreview && shouldComposeBulkSequence
    },
    {
      mode: 'menu',
      props: {
        className: classes.primaryButton,
        disabled: bulkEmailStatus.loading,
        icon: SubmitButtonIcon,
        iconPosition: 'right',
        menuItems: SubmitBulkOptions,
        menuProps: {
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          },
          transformOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          },
          classes: { paper: null }
        },
        text: bulkEmailInPreview.edit ? drawerCopies.buttons.save : drawerCopies.buttons.sendBulk
      },
      visible: !shouldComposeBulkSequence
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{
        avatar: uiState.shouldShowSequencesPreview ? (
          <FPIconButton
            disabled={bulkEmailStatus.loading}
            icon={SvgArrowLeft}
            iconMode="custom"
            onClick={togglers.sequencesPreview(false)}
            tooltipProps={{ title: drawerCopies.buttons.goBack }}
          />
        ) : null,
        title: uiState.shouldShowSequencesPreview
          ? drawerCopies.titles.sequencePreview
          : drawerCopies.titles.bulkEmail,
        titleTypographyProps: { variant: 'h5' }
      }}
      contentContainerClassName={commonClasses.contentContainer}
      ContentWrapper={Grid}
      ContentWrapperProps={{ ref: contentWrapperRef }}
      FPDrawerProps={{
        open: true,
        classes: { paper: commonClasses.drawerRoot }
      }}
      hasPaddingContent={false}
      onClose={handlers.onCancel}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={drawerButtonDefinitions}
        />
      }
    >
      <StatusManager status={uiState.isFormLoading ? UIStatus.Loading : UIStatus.Success}>
        <FormContext {...form}>
          <When condition={sendDate && bulkEmailInPreview.edit}>
            <ScheduleInfoAlert
              classes={{ scheduleInfo: classes.scheduleInfo }}
              onOpenScheduleDialog={handlers.onSchedule}
              sendDate={sendDate}
            />
          </When>
          <When condition={bulkRestrictionRule}>
            <RestrictionDialog
              onOpenScheduleDialog={handleTimeRestrictionDialogReschedule}
              onClose={() => dispatch(setBulkRestrictionToInitial())}
              onSaveAsDraft={handleSaveDraftDialog}
              onSubmitBulkEmail={handleSubmitBulkEmail}
              bulkRestriction={bulkEmailStatus.bulkRestriction}
              isSendScheduleNow={sendDate && bulkEmailInPreview.edit}
            />
          </When>
          <When condition={uiState.shouldShowDiscardDialog}>
            <DiscardBulkDialog
              isBulkBeingEdited={isScheduleBeingEdited || isDraftBeingEdited}
              onDiscard={handleDiscardBulk}
              onKeepEditing={handleToggleDiscardDialog(false)}
              // TODO: verify why save changes button was disabled when editing a scheduled bulk (not changing it in the meantime)
              // TODO: check draft flow for sequences, meanwhile disable button to save sequence draft
              onSaveChanges={
                isScheduleBeingEdited || shouldComposeBulkSequence ? null : handleSaveDraftDialog
              }
              selectedMenuItem={selectedMenuItem}
            />
          </When>
          <When condition={uiState.shouldShowSequencesPreview}>
            <BulkEmailDrawerSequencesPreview />
          </When>
          <Collapse in={!uiState.shouldShowSequencesPreview} timeout={0}>
            <BulkEmailForm
              bulkQuota={bulkQuota}
              shouldComposeBulkSequence={shouldComposeBulkSequence}
            />
          </Collapse>
          <When condition={uiState.shouldShowScheduleDialog}>
            <ScheduleDialog
              shouldUnregisterFieldsOnClose={!isScheduleBeingEdited}
              onClose={togglers.scheduleDialog(false)}
              onSubmitBulkEmail={handleSubmitBulkEmail}
              remainingQuota={remainingQuota}
              searchProjectSize={searchProjectSize}
              notifyBulkQuotaRestriction={notifyBulkQuotaRestriction}
            />
          </When>
        </FormContext>
      </StatusManager>
    </FPDrawer>
  );
};

export default memo(BulkEmailDrawer);
